import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import dnPopup from "./utils/dnPopup";
import { FaUserCircle, FaClock, FaCamera, FaSignOutAlt, FaTimes, FaChevronDown } from "react-icons/fa";
import { apiFetch, EMP_PROFILE_FILE_BASE } from "./utils/api";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./Header.css";

const MAX_FILE_SIZE = 0.5 * 1024 * 1024; // 0.5 MB in bytes

const formatTime = (sec) => {
    const h = String(Math.floor(sec / 3600)).padStart(2, "0");
    const m = String(Math.floor((sec % 3600) / 60)).padStart(2, "0");
    const s = String(sec % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
};

// Deterministic fallback avatar colour when the employee has no profile_color.
function stringToColor(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = "#";
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xff;
        color += ("00" + value.toString(16)).slice(-2);
    }
    return color;
}

const profileImgSrc = (img) =>
    img.startsWith("data:") ? img : `${EMP_PROFILE_FILE_BASE}/${img.replace(/\\/g, "/")}`;

function Header() {
    const [isPunchedIn, setIsPunchedIn] = useState(false);
    const [seconds, setSeconds] = useState(0);
    const [history, setHistory] = useState({});
    const [showModal, setShowModal] = useState(false);
    // Absolute timestamp (ms) of the active punch-in — drives the timer so it
    // survives reloads / inactive tabs (recomputed from the server on mount).
    const [punchInAt, setPunchInAt] = useState(null);
    const [loggedInEmp, setLoggedInEmp] = useState(null);
    const [noShift, setNoShift] = useState(false); // non-admin employee with no shift → hide punch UI
    const [showEmpMenu, setShowEmpMenu] = useState(false);
    // Scoped to the dropdown's big avatar only. It used to be shared with the
    // small header avatar, which then showed a camera on hover even though
    // clicking it opens the menu, not the uploader.
    const [isDropdownHovered, setIsDropdownHovered] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    // In-flight guard so a double-click can't fire two punch requests and let
    // the slower response decide the final UI state.
    const [punchBusy, setPunchBusy] = useState(false);
    const modalRef = useRef(null);
    const fileInputRef = useRef(null);
    const empMenuRef = useRef(null);
    const wasPunchedRef = useRef(false); // tracks last known punched-in state
    const navigate = useNavigate();

    const fetchPunchHistory = useCallback(async () => {
        try {
            const res = await apiFetch(`/api/punch/history`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await res.json();

            // Group data by date (e.g., "Monday (16-10-2025)")
            const grouped = {};

            data.forEach(entry => {
                const dateObj = new Date(entry.punch_in);
                const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
                const dateFormatted = dateObj.toLocaleDateString('en-GB'); // dd/mm/yyyy
                const key = `${dayName} (${dateFormatted})`;

                if (!grouped[key]) grouped[key] = [];

                grouped[key].push({
                    start: new Date(entry.punch_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    end: entry.punch_out
                        ? new Date(entry.punch_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        : "—",
                    duration: entry.punch_out
                        ? formatTime(Math.floor((new Date(entry.punch_out) - new Date(entry.punch_in)) / 1000))
                        : "—"
                });
            });

            setHistory(grouped);
        } catch (err) {
            console.error("Error fetching punch history:", err);
        }
    }, []);

    // Restore punch state from the server (source of truth). Polled, so a
    // server-side close (e.g. auto punch-out at shift end + 4h) syncs the UI.
    const fetchPunchStatus = useCallback(async () => {
        try {
            const res = await apiFetch(`/api/punch/status`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await res.json();
            if (data.active) {
                setPunchInAt(Date.now() - (data.elapsed_seconds || 0) * 1000);
                setIsPunchedIn(true);
                wasPunchedRef.current = true;
            } else {
                // We thought we were punched in but the server says we're not →
                // the punch was closed elsewhere (auto punch-out). Sync + refresh.
                if (wasPunchedRef.current) {
                    fetchPunchHistory();
                    window.dispatchEvent(new Event("dtime:punch-changed"));
                }
                wasPunchedRef.current = false;
                setIsPunchedIn(false);
                setPunchInAt(null);
                setSeconds(0);
            }
        } catch (err) {
            console.error("Error fetching punch status:", err);
        }
    }, [fetchPunchHistory]);

    // A non-admin employee with no shift can't punch — show a contact-admin note.
    const fetchShiftState = useCallback(async () => {
        try {
            const res = await apiFetch(`/api/shift/my-shift`, { method: 'GET', credentials: 'include' });
            if (!res.ok) { setNoShift(false); return; } // fail open on server error
            const data = await res.json();
            setNoShift(!data?.has_shift);
        } catch (err) {
            setNoShift(false); // fail open
        }
    }, []);

    const fetchloginedEmployees = useCallback(async () => {
        try {
            const res = await apiFetch(`/api/employee/logined_employee`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await res.json();

            if (data?.message === "Access Denied. No Token Provided!" ||
                data?.message === "Invalid Token") {
                navigate("/login");
                return;
            }

            if (data[0].emp_id) {
                setLoggedInEmp(data[0]);
                fetchPunchHistory();
                fetchPunchStatus();
                // Admin / Sub Admin bypass the shift lock entirely.
                const role = data[0].emp_access_level;
                if (role === "Admin" || role === "Sub Admin") {
                    setNoShift(false);
                } else {
                    fetchShiftState();
                }
            }

        } catch (err) {
            console.error("Error fetching employees:", err);
        }
    }, [navigate, fetchPunchHistory, fetchPunchStatus, fetchShiftState]);

    useEffect(() => {
        fetchloginedEmployees();
    }, [fetchloginedEmployees]);

    // Timer derives elapsed seconds from the absolute punch-in time on every tick,
    // so it stays accurate across reloads, background tabs, and clock jumps.
    useEffect(() => {
        if (!isPunchedIn || !punchInAt) return;

        const tick = () => setSeconds(Math.max(0, Math.floor((Date.now() - punchInAt) / 1000)));
        tick(); // update immediately, don't wait 1s
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [isPunchedIn, punchInAt]);

    // Poll punch status every 60s so an auto punch-out (or a punch from another
    // tab/device) reflects here without needing a page reload.
    useEffect(() => {
        const id = setInterval(() => { fetchPunchStatus(); }, 60000);
        return () => clearInterval(id);
    }, [fetchPunchStatus]);

    // Close the profile menu on an outside click or Escape. Without this it
    // stayed open until you clicked the avatar again.
    useEffect(() => {
        if (!showEmpMenu) return;
        const onPointerDown = (e) => {
            if (empMenuRef.current && !empMenuRef.current.contains(e.target)) setShowEmpMenu(false);
        };
        const onKeyDown = (e) => { if (e.key === "Escape") setShowEmpMenu(false); };
        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [showEmpMenu]);

    // Escape closes whichever modal is open.
    useEffect(() => {
        if (!showModal && !isModalOpen) return;
        const onKeyDown = (e) => {
            if (e.key !== "Escape") return;
            setShowModal(false);
            setIsModalOpen(false);
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [showModal, isModalOpen]);

    const handlePunch = async () => {
        if (punchBusy) return;
        setPunchBusy(true);
        try {
            if (!isPunchedIn) {
                // Punch In
                const res = await apiFetch("/api/punch/punch-in", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: 'include',
                });
                const data = await res.json();
                if (data.success) {
                    // Anchor the timer to the server's elapsed time (0 for a fresh punch-in)
                    setPunchInAt(Date.now() - (data.elapsed_seconds || 0) * 1000);
                    setIsPunchedIn(true);
                    wasPunchedRef.current = true;
                    fetchPunchHistory();
                    window.dispatchEvent(new Event("dtime:punch-changed"));
                }
            } else {
                // Punch Out
                const res = await apiFetch("/api/punch/punch-out", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: 'include',
                });
                const data = await res.json();
                if (data.success) {
                    setIsPunchedIn(false);
                    setPunchInAt(null);
                    setSeconds(0);
                    wasPunchedRef.current = false;
                    fetchPunchHistory(); // refresh from server (source of truth)
                    window.dispatchEvent(new Event("dtime:punch-changed"));
                }
            }
        } catch (err) {
            console.error("Punch failed:", err);
            dnPopup.fire({
                icon: "error",
                title: "Punch Failed",
                text: "Could not reach the server. Please try again.",
            });
        } finally {
            setPunchBusy(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // ✅ Check file size
        if (file.size > MAX_FILE_SIZE) {
            dnPopup.fire({
                icon: "warning",
                title: "File Too Large",
                text: "Please select an image smaller than 0.5 MB.",
            });
            // Reset selected file and input
            setSelectedFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        setSelectedFile(file);
    };

    const handleSaveProfileImage = async () => {
        if (!selectedFile) {
            dnPopup.fire({
                icon: "warning",
                title: "No File Selected",
                text: "Please select a profile image before saving.",
            });
            return;
        }

        // ✅ Optional: double-check file size before sending
        if (selectedFile.size > MAX_FILE_SIZE) {
            dnPopup.fire({
                icon: "warning",
                title: "File Too Large",
                text: "Please select an image smaller than 0.5 MB.",
            });
            return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append("profile", selectedFile);

        try {
            const res = await apiFetch(`/api/employee/upload-profile`, {
                method: "POST",
                body: formDataToSend,
                credentials: 'include',
            });

            const data = await res.json();

            if (!res.ok) throw new Error(data.error || "Failed to upload profile image");

            dnPopup.fire({
                icon: "success",
                title: "Profile Updated",
                text: "Your profile image has been updated successfully!",
            });

            // Close modal after success
            setIsModalOpen(false);

            // Update logged in user state
            setLoggedInEmp(prev => ({ ...prev, emp_profile_img: data.profilePath }));

            // Reset file input
            setSelectedFile(null);
            if (fileInputRef.current) fileInputRef.current.value = null;

        } catch (err) {
            dnPopup.fire({
                icon: "error",
                title: "Upload Error",
                text: err.message,
            });
        }
    };

    const handleLogout = async () => {
        await apiFetch(`/api/login/logout`, {
            method: "POST",
            credentials: "include",
        });

        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/login";
    };

    const fullName = loggedInEmp
        ? `${loggedInEmp.emp_first_name ?? ""} ${loggedInEmp.emp_last_name ?? ""}`.trim()
        : "";
    // String() guards a null first/last name - this runs during render, so a
    // throw here white-screens the whole app.
    const initials = loggedInEmp
        ? loggedInEmp.profile_letters ??
        (String(loggedInEmp.emp_first_name ?? "").charAt(0) + String(loggedInEmp.emp_last_name ?? "").charAt(0)).toUpperCase()
        : "";
    const avatarColor = loggedInEmp
        ? (loggedInEmp.emp_profile_img ? "transparent" : loggedInEmp.profile_color ?? stringToColor(fullName))
        : "transparent";
    const hasHistory = Object.keys(history).length > 0;

    return (
        <header className="app-header">
            <div className="header-left">
                <Link className="hdr-brand" to="/Dashboard">
                    <img src={logo_eagle} alt="" />
                    <span className="hdr-brand-text">dTime</span>
                </Link>
            </div>

            <div className="header-center">
                {noShift ? (
                    <span className="no-shift-note">
                        You don't have a shift assigned. Please contact admin@dolluzcorp.com
                    </span>
                ) : (
                    <>
                        {/* The hidden sizer holds the button at the width of the
                            LONGER label, so swapping In/Out can't resize it and
                            shove the timer sideways - and it does that at the
                            real rendered text width, with no magic min-width. */}
                        <button
                            onClick={handlePunch}
                            disabled={punchBusy}
                            className={`punch-btn ${isPunchedIn ? "punch-out" : "punch-in"}`}
                        >
                            <span>{isPunchedIn ? "Punch Out" : "Punch In"}</span>
                            <span className="punch-btn-sizer" aria-hidden="true">Punch Out</span>
                        </button>

                        <span className="timer" role="timer" aria-label="Time since punch in">
                            {isPunchedIn || seconds > 0 ? formatTime(seconds) : "00:00:00"}
                        </span>

                        {/* Always rendered - it only becomes visible once there is
                            history. Conditionally rendering it made the whole
                            centred cluster jump sideways on the first punch-in. */}
                        <button
                            type="button"
                            className={`hdr-icon-btn${hasHistory ? "" : " is-placeholder"}`}
                            onClick={() => setShowModal(true)}
                            title="Punch history"
                            aria-label="Punch history"
                            aria-hidden={hasHistory ? undefined : true}
                            tabIndex={hasHistory ? 0 : -1}
                        >
                            <FaClock />
                        </button>
                    </>
                )}
            </div>

            <div className="header-right">
                {loggedInEmp ? (
                    <div className="emp-menu" ref={empMenuRef}>
                        <button
                            type="button"
                            className="emp-trigger"
                            onClick={() => setShowEmpMenu(v => !v)}
                            aria-haspopup="menu"
                            aria-expanded={showEmpMenu}
                        >
                            <span className="user-name" title={fullName}>
                                Welcome, {fullName}
                            </span>
                            <span
                                className="emp-circle"
                                style={{ backgroundColor: avatarColor }}
                            >
                                {loggedInEmp.emp_profile_img ? (
                                    <img src={profileImgSrc(loggedInEmp.emp_profile_img)} alt="" />
                                ) : initials}
                            </span>
                            <FaChevronDown className={`emp-caret${showEmpMenu ? " open" : ""}`} />
                        </button>

                        {showEmpMenu && (
                            <div className="emp-dropdown" role="menu">
                                {/* Profile Info */}
                                <div className="emp-profile">
                                    <button
                                        type="button"
                                        className="emp-profile-circle"
                                        style={{ backgroundColor: avatarColor }}
                                        onMouseEnter={() => setIsDropdownHovered(true)}
                                        onMouseLeave={() => setIsDropdownHovered(false)}
                                        onClick={() => { setShowEmpMenu(false); setIsModalOpen(true); }}
                                        title="Change profile image"
                                        aria-label="Change profile image"
                                    >
                                        {isDropdownHovered ? (
                                            <FaCamera color="white" />
                                        ) : loggedInEmp.emp_profile_img ? (
                                            <img src={profileImgSrc(loggedInEmp.emp_profile_img)} alt="" />
                                        ) : initials}
                                    </button>

                                    <div className="emp-profile-text">
                                        <div className="emp-name" title={fullName}>{fullName}</div>
                                        <div className="emp-org">One Place. One start. One Team.</div>
                                    </div>
                                </div>

                                {/* Dropdown actions */}
                                <button
                                    onClick={() => { setShowEmpMenu(false); navigate('/login?changePassword'); }}
                                    className="emp-dropdown-btn"
                                    role="menuitem"
                                >
                                    <i className="fa fa-key"></i> Change Password
                                </button>
                                <button
                                    onClick={handleLogout}
                                    className="emp-dropdown-btn logout-btn"
                                    role="menuitem"
                                >
                                    <FaSignOutAlt className="logout-icon" /> Logout
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <span className="emp-trigger is-loading">
                        <span className="user-name">Welcome, Employee</span>
                        <FaUserCircle className="user-icon" />
                    </span>
                )}
            </div>

            {/* Both modals are portalled to <body>. Rendered inside <header> they
                sat in the header's own stacking context (position:fixed +
                z-index:1000), and inherited its color:#fff - which is why the
                punch-history date headings were white on a white card. */}
            {isModalOpen && loggedInEmp && createPortal(
                <div className="profile-modal-overlay" onClick={() => setIsModalOpen(false)}>
                    <div className="profile-modal" ref={modalRef} onClick={(e) => e.stopPropagation()}>
                        <button
                            className="profile-modal-close"
                            onClick={() => setIsModalOpen(false)}
                            aria-label="Close"
                        >
                            ✖
                        </button>

                        <h3 className="profile-modal-title">Update Profile Image</h3>

                        <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
                            {selectedFile ? (
                                <img
                                    src={URL.createObjectURL(selectedFile)}
                                    alt="Preview"
                                    className="profile-image-preview"
                                />
                            ) : (
                                <FaCamera size={60} color="#555" />
                            )}
                        </div>

                        <div style={{ position: "relative", width: "100%" }}>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                style={{ width: "100%", paddingRight: selectedFile ? "30px" : "0" }}
                            />

                            {selectedFile && (
                                <button
                                    type="button"
                                    className="profile-file-clear"
                                    aria-label="Clear selected file"
                                    onClick={() => {
                                        setSelectedFile(null);
                                        if (fileInputRef.current) {
                                            fileInputRef.current.value = "";
                                        }
                                    }}
                                >
                                    ✖
                                </button>
                            )}
                        </div>

                        <div className="profile-modal-buttons">
                            <button
                                className="profile-modal-save"
                                onClick={handleSaveProfileImage}
                                disabled={!selectedFile}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {showModal && createPortal(
                <div className="modal" onClick={() => setShowModal(false)}>
                    <div className="modal-body" onClick={(e) => e.stopPropagation()}>
                        <button className="punch-modal-close" onClick={() => setShowModal(false)} title="Close" aria-label="Close">
                            <FaTimes />
                        </button>
                        <h3>Punch History</h3>
                        {!hasHistory ? (
                            <p className="punch-empty">No history found.</p>
                        ) : (Object.keys(history).map((dateKey, idx) => (
                            <div key={idx} className="modal-content">
                                <strong>{dateKey}</strong>
                                <ul> {history[dateKey].map((entry, i) => (
                                    <li key={i} className="punch-entry">
                                        <span className="pe-item">
                                            <span className="pe-label">Punch In</span>
                                            <span className="pe-val pe-in">{entry.start}</span>
                                        </span>
                                        <span className="pe-sep">|</span>
                                        <span className="pe-item">
                                            <span className="pe-label">Punch Out</span>
                                            <span className="pe-val pe-out">{entry.end}</span>
                                        </span>
                                        <span className="pe-sep">|</span>
                                        <span className="pe-item">
                                            <span className="pe-label">Duration</span>
                                            <span className="pe-val pe-dur">{entry.duration}</span>
                                        </span>
                                    </li>))}
                                </ul>
                            </div>)))}
                        <button className="punch-modal-dismiss" onClick={() => setShowModal(false)}>Close</button>
                    </div>
                </div>,
                document.body
            )}
        </header>
    );
}

export default Header;
